import * as React from 'react';
import {
  Button,
  Input,
  Select,
  makeStyles,
  tokens,
  Field,
  MessageBar,
  MessageBarBody,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { Event, EventCreate, EventoEstrutura } from '../../../types/event';
import { ApiErrorInfo } from '../../../types/api';
import { sqlQueryValidation } from '../../../utils/validationUtils';
import { useMarker } from '../../../hooks/useMarker';
import { getSeverityOptions } from '../../../utils/severityHelpers';
import EventStructuresGrid from './EventStructuresGrid';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: tokens.spacingHorizontalL,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalL,
  },
});

interface EventFormProps {
  event?: Event;
  onSave: (event: EventCreate) => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ApiErrorInfo | null;
}

const EventForm: React.FC<EventFormProps> = ({
  event,
  onSave,
  onCancel,
  loading,
  error
}) => {
  const styles = useStyles();
  const { markers, fetchMarkers } = useMarker();

  // Carregar marcadores quando o componente montar
  React.useEffect(() => {
    fetchMarkers();
  }, [fetchMarkers]);

  // Atualizar formData quando o event prop mudar (útil para edição assíncrona)
  React.useEffect(() => {
    if (event) {
      setFormData({
        erp: event.erp || 1,
        descricao: event.descricao || '',
        detalhe: event.detalhe || '',
        severidade: event.severidade || 0,
        tipo: event.tipo || 1,
        pontuacao: 1,
        consulta: event.consulta || '',
        consultaERPId: event.consultaERPId || 0,
        consultaId: event.consultaId || 0,
        marcadorId: event.marcadorId || 0,
        estruturaEventos: event.estruturaEventos || [],
      });
    }
  }, [event]);

  const [formData, setFormData] = React.useState<EventCreate>(
    event ? {
      erp: event.erp || 1,
      descricao: event.descricao || '',
      detalhe: event.detalhe || '',
      severidade: event.severidade || 0,
      tipo: event.tipo || 1,
      pontuacao: 1,
      consulta: event.consulta || '',
      consultaERPId: event.consultaERPId || 0,
      consultaId: event.consultaId || 0,
      marcadorId: event.marcadorId || 0,
      estruturaEventos: event.estruturaEventos || [],
    } : {
      erp: 1,
      descricao: '',
      detalhe: '',
      severidade: 0,
      tipo: 1,
      pontuacao: 1,
      consulta: '',
      consultaERPId: 0,
      consultaId: 0,
      marcadorId: 0,
      estruturaEventos: [],
    }
  );

  const [fieldErrors, setFieldErrors] = React.useState({
    descricao: '',
    consulta: '',
  });

  const validateField = (field: keyof typeof fieldErrors, value: string, touched: boolean = false) => {
    let error = '';

    switch (field) {
      case 'descricao':
        if (!value.trim()) {
          error = 'Descrição é obrigatória';
        }
        break;
      case 'consulta':
        const sqlValidation = sqlQueryValidation.validate(value);
        if (!sqlValidation.isValid) {
          error = sqlValidation.errorMessage || 'Consulta SQL inválida';
        }
        break;
    }

    setFieldErrors(prev => ({ ...prev, [field]: touched ? error : '' }));
    return error === '';
  };

  const handleInputChange = (field: keyof EventCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Validar campo em tempo real apenas se já foi tocado
    if (fieldErrors[field as keyof typeof fieldErrors]) {
      validateField(field as keyof typeof fieldErrors, value, true);
    }
  };

  const handleBlur = (field: keyof typeof fieldErrors) => {
    validateField(field, formData[field as keyof typeof formData] as string, true);
  };

  const handleAddEstrutura = () => {
    const newEstrutura: EventoEstrutura = {
      estruturaId: formData.estruturaEventos.length + 1, // Index da linha (começando do 1)
      severidade: 0,
      detalhe: '',
      pontuacao: 0,
      regraComparacao: 1, // Valor padrão: Maior
      meta: 0,
    };
    setFormData(prev => ({
      ...prev,
      estruturaEventos: [...prev.estruturaEventos, newEstrutura],
    }));
  };

  const handleRemoveEstrutura = (index: number) => {
    setFormData(prev => {
      // Remover a estrutura no índice especificado
      const filteredEstruturas = prev.estruturaEventos.filter((_, i) => i !== index);

      // Reordenar os estruturaId para manter sequência contínua
      const updatedEstruturas = filteredEstruturas.map((estrutura, i) => ({
        ...estrutura,
        estruturaId: i + 1,
      }));

      return {
        ...prev,
        estruturaEventos: updatedEstruturas,
      };
    });
  };

  const handleUpdateEstrutura = (index: number, field: keyof EventoEstrutura, value: any) => {
    setFormData(prev => ({
      ...prev,
      estruturaEventos: prev.estruturaEventos.map((estrutura, i) =>
        i === index ? { ...estrutura, [field]: value } : estrutura
      ),
    }));
  };

  const handleMoveEstrutura = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= formData.estruturaEventos.length) return;

    setFormData(prev => {
      const newEstruturas = [...prev.estruturaEventos];
      const [movedItem] = newEstruturas.splice(fromIndex, 1);
      newEstruturas.splice(toIndex, 0, movedItem);

      // Atualizar os estruturaId conforme a nova ordem
      const updatedEstruturas = newEstruturas.map((estrutura, index) => ({
        ...estrutura,
        estruturaId: index + 1,
      }));

      return {
        ...prev,
        estruturaEventos: updatedEstruturas,
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Marcar todos os campos como tocados e validar
    setFieldErrors({
      descricao: formData.descricao.trim() ? '' : 'Descrição é obrigatória',
      consulta: formData.consulta.trim() ? '' : 'Consulta SQL é obrigatória',
    });

    // Verificar se há erros
    const hasErrors = !formData.descricao.trim() || !formData.consulta.trim();

    if (hasErrors) {
      return;
    }

    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      {loading && (
        <MessageBar intent="info">
          <MessageBarBody>
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
              <Spinner size="tiny" />
              Estamos salvando o evento. Isso pode levar alguns segundos...
            </div>
          </MessageBarBody>
        </MessageBar>
      )}

      {error && (
        <MessageBar intent={error.isAuthError ? "warning" : "error"}>
          <MessageBarBody>
            {error.isNotFound && "Evento não encontrado. Verifique se o ID está correto."}
            {error.isValidationError && error.message}
            {error.isAuthError && "Acesso negado. Você não tem permissão para esta operação."}
            {!error.isNotFound && !error.isValidationError && !error.isAuthError && error.message}
          </MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.section}>
        <div className={styles.row}>
          <Field
            label="Sistema ERP"
            required
          >
            <Select
              size="large"
              value={formData.erp.toString()}
              onChange={(e, data) => handleInputChange('erp', parseInt(data.value || '1'))}
            >
              <option value="1">ERP UAU</option>
            </Select>
          </Field>

          <Field
            label="Tipo"
            required
          >
            <Select
              size="large"
              value={formData.tipo?.toString() || '1'}
              onChange={(e, data) => handleInputChange('tipo', parseInt(data.value || '1'))}
            >
              <option value="1">Livre</option>
              <option value="2">Diagnóstico</option>
            </Select>
          </Field>

          <Field
            label="Severidade"
            required
          >
            <Select
              size="large"
              value={formData.severidade?.toString() || '0'}
              onChange={(e, data) => handleInputChange('severidade', parseInt(data.value || '0'))}
            >
              {getSeverityOptions().map((option) => (
                <option key={option.value} value={option.value.toString()}>
                  {option.label}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className={styles.row}>
          <Field
            label="Consulta ERP ID"
          >
            <Input
              size="large"
              type="number"
              value={formData.consultaERPId?.toString() || ''}
              onChange={(e) => handleInputChange('consultaERPId', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </Field>

          <div></div> {/* Espaço vazio para manter alinhamento */}
          <div></div> {/* Espaço vazio para manter alinhamento */}
        </div>

        <Field
          label="Descrição"
          required
          validationMessage={fieldErrors.descricao}
          validationState={fieldErrors.descricao ? 'error' : 'none'}
        >
          <Input
            size="large"
            value={formData.descricao}
            onChange={(e) => handleInputChange('descricao', e.target.value)}
            onBlur={() => handleBlur('descricao')}
            placeholder="Descrição do evento"
          />
        </Field>

        <Field label="Detalhe">
          <Textarea
            size="large"
            value={formData.detalhe}
            onChange={(e) => handleInputChange('detalhe', e.target.value)}
            placeholder="Detalhes adicionais do evento"
            rows={3}
            resize="vertical"
          />
        </Field>

        <Field
          label="Consulta SQL"
          required
          validationMessage={fieldErrors.consulta}
          validationState={fieldErrors.consulta ? 'error' : 'none'}
        >
          <Textarea
            size="large"
            value={formData.consulta}
            onChange={(e) => handleInputChange('consulta', e.target.value)}
            onBlur={() => handleBlur('consulta')}
            placeholder="Digite apenas consultas SELECT. Exemplo: SELECT * FROM tabela WHERE condicao = 'valor'"
            rows={6}
            resize="vertical"
          />
        </Field>

        <Field label="Marcador">
          <Select
            size="large"
            value={formData.marcadorId.toString()}
            onChange={(e, data) => handleInputChange('marcadorId', parseInt(data.value || '0'))}
          >
            <option value="0">Nenhum</option>
            {markers.map((marker) => (
              <option key={marker.id} value={marker.id.toString()}>
                {marker.descricao}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <EventStructuresGrid
        estruturas={formData.estruturaEventos}
        onAddEstrutura={handleAddEstrutura}
        onRemoveEstrutura={handleRemoveEstrutura}
        onUpdateEstrutura={handleUpdateEstrutura}
        onReorderEstrutura={handleMoveEstrutura}
      />

      <div className={styles.actions}>
        <Button appearance="subtle" onClick={onCancel}>
          Cancelar
        </Button>
        <Button
          type="submit"
          appearance="primary"
          disabled={loading || Object.values(fieldErrors).some(error => error !== '')}
        >
          {loading ? 'Salvando...' : event ? 'Atualizar' : 'Criar'} Evento
        </Button>
      </div>
    </form>
  );
};

export default EventForm;